import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, Camera, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const SESSION_KEY = "exit_intent_shown";

// Marketing-only: never fire over the app, auth, settings, the demo it sends to,
// or client portals. (LeadMagnetPopup has the same guard; ExitIntent was missing
// it and would otherwise pop over the editor for anonymous /demo visitors.)
const MARKETING_PATH_BLACKLIST = [
  "/dashboard",
  "/cctv-design",
  "/demo",
  "/auth",
  "/settings",
  "/p/",
];

export const ExitIntentPopup = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const handleCTA = useCallback(async () => {
    const trimmed = email.trim();
    // Basic email shape check — without it the popup happily upserts "a",
    // "junk", "test" into the leads table. Pattern is intentionally lax
    // (one @, one ., no spaces) — matches HTML5 type=email's permissive
    // semantics, just enough to reject obvious garbage.
    const isPlausibleEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    // Only persist the email when the visitor explicitly opted in (GDPR). The
    // "try the tool" navigation happens regardless, so a no-consent click still
    // takes them to the demo — we just don't add them to the list.
    if (isPlausibleEmail && consent) {
      setLoading(true);
      try {
        const { error } = await supabase.from("leads").upsert(
          {
            email: trimmed.toLowerCase(),
            source: "exit_intent",
            page_url: window.location.pathname,
          },
          { onConflict: "email" }
        );
        if (error) throw error;
        try { localStorage.setItem("lead_email_captured", "1"); } catch { /* private mode */ }
      } catch (err) {
        console.error("Failed to save lead:", err);
        toast.error(t('eip_error'));
      } finally {
        setLoading(false);
      }
    }
    setVisible(false);
    navigate("/demo");
  }, [navigate, email, consent, t]);

  useEffect(() => {
    // Don't show on mobile (coarse pointer = touch device)
    const isMobile =
      window.matchMedia("(pointer: coarse)").matches ||
      "ontouchstart" in window;
    if (isMobile) return;

    // Don't show for authenticated users
    if (user) return;

    // Don't show if already shown this session
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Marketing pages only (mirrors LeadMagnetPopup's blacklist)
    const path = window.location.pathname;
    if (MARKETING_PATH_BLACKLIST.some((p) => path.startsWith(p))) return;

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger when cursor leaves through the top of the viewport
      if (e.clientY > 0) return;

      sessionStorage.setItem(SESSION_KEY, "1");
      setVisible(true);

      // Remove listener after triggering once
      document.documentElement.removeEventListener(
        "mouseleave",
        handleMouseLeave
      );
    };

    document.documentElement.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.documentElement.removeEventListener(
        "mouseleave",
        handleMouseLeave
      );
    };
  }, [user]);

  return (
    <>
      {visible && (
        <div
          className="todai-cinematic fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(7, 6, 9, 0.72)' }}
          onClick={handleClose}
        >
          <div
            className="relative w-full max-w-md p-8"
            style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius-card, 14px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={t('eip_close')}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Icon */}
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-brand-blue)]/10">
              <Camera className="h-7 w-7 text-[var(--color-brand-blue)]" />
            </div>

            {/* Headline */}
            <h2 className="text-center text-xl font-bold text-foreground sm:text-2xl">
              {t('eip_headline')}
            </h2>

            {/* Subtext */}
            <p className="mt-3 text-center text-sm text-muted-foreground leading-relaxed">
              {t('eip_subtext')}
            </p>

            {/* Email input */}
            <input
              type="email"
              placeholder={t('eip_email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-6 w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-[var(--color-brand-blue)] focus:ring-1 focus:ring-[var(--color-brand-blue)]/20 transition-colors"
            />

            {/* Optional opt-in — only gates saving the email; the demo CTA works
                without it. EU-heavy traffic, GDPR. */}
            <label className="mt-3 flex items-start gap-2 text-xs text-muted-foreground leading-snug cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 flex-shrink-0 accent-[var(--color-brand-blue)]"
              />
              <span>
                {t("eip_consent", { defaultValue: "Email me occasional CCTV design tips (optional). Unsubscribe anytime." })}
              </span>
            </label>

            {/* CTA */}
            <Button
              onClick={handleCTA}
              disabled={loading}
              className="mt-3 w-full bg-[var(--color-brand-blue)] hover:bg-[var(--color-brand-blue-hover)] text-white font-semibold py-2.5"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('eip_cta_button')
              )}
            </Button>

            {/* No thanks */}
            <button
              onClick={handleClose}
              className="mt-3 block w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('eip_no_thanks')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
